import React, { useMemo, useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { QrCode, Download, Copy, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { listTrackedLinks, shortLinkUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useEffect } from 'react';

export default function QrTool() {
  const { user } = useApp();
  const [value, setValue] = useState('https://vidcreforge.com/');
  const [bg, setBg] = useState('#0a0a0c');
  const [fg, setFg] = useState('#ffffff');
  const [size, setSize] = useState(320);
  const [level, setLevel] = useState('M');
  const [links, setLinks] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    listTrackedLinks().then(ls => setLinks(ls || [])).catch(() => {});
  }, [user]);

  const fileName = useMemo(() => `vidhub-qr-${Date.now()}.png`, []);

  const downloadPng = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return toast.error('Canvas not ready');
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName;
    document.body.appendChild(link); link.click(); link.remove();
  };

  const downloadSvg = () => {
    const svg = document.getElementById('qr-svg');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = fileName.replace('.png', '.svg');
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(url);
  };

  const doCopy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success('URL copied'); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <div data-testid="qr-tool-page">
      <div className="flex items-center gap-2 text-[#a78bfa]"><QrCode className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">QR Code Tool</span></div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-2 leading-none">Generate a QR code</h1>
      <p className="text-white/60 text-[14px] mt-1">Paste any link (or pick one of your tracked links) and download as PNG or SVG.</p>

      <div className="grid lg:grid-cols-[1fr_420px] gap-6 mt-8">
        <div className="card-shell p-5 space-y-4">
          <label className="block">
            <span className="text-[13px] text-white/70 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> URL or text</span>
            <input value={value} onChange={e => setValue(e.target.value)} className="mt-1 input-field w-full" placeholder="https://…" data-testid="qr-input" />
          </label>

          {links.length > 0 && (
            <div>
              <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">Your tracked links</div>
              <div className="flex flex-wrap gap-1.5">
                {links.slice(0, 20).map(l => (
                  <button key={l.id} onClick={() => setValue(shortLinkUrl(l.slug))}
                    className="chip text-[11.5px]" title={l.destination_url}
                    data-testid={`qr-picklink-${l.slug}`}>
                    /l/{l.slug} {l.source && <span className="text-white/45">• {l.source}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-[12px] text-white/60">Foreground</span>
              <input type="color" value={fg} onChange={e => setFg(e.target.value)} className="mt-1 h-9 w-full rounded bg-transparent border border-white/10" data-testid="qr-fg" />
            </label>
            <label className="block">
              <span className="text-[12px] text-white/60">Background</span>
              <input type="color" value={bg} onChange={e => setBg(e.target.value)} className="mt-1 h-9 w-full rounded bg-transparent border border-white/10" data-testid="qr-bg" />
            </label>
            <label className="block">
              <span className="text-[12px] text-white/60">Size (px)</span>
              <input type="number" min="128" max="1024" step="16" value={size} onChange={e => setSize(parseInt(e.target.value) || 320)} className="mt-1 input-field w-full" data-testid="qr-size" />
            </label>
            <label className="block">
              <span className="text-[12px] text-white/60">Error-correction</span>
              <select value={level} onChange={e => setLevel(e.target.value)} className="mt-1 input-field w-full">
                <option value="L">L — 7%</option><option value="M">M — 15%</option><option value="Q">Q — 25%</option><option value="H">H — 30%</option>
              </select>
            </label>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={doCopy} className="btn-ghost" data-testid="qr-copy"><Copy className="w-4 h-4" /> Copy URL</button>
            <button onClick={downloadPng} className="btn-primary" data-testid="qr-download-png"><Download className="w-4 h-4" /> Download PNG</button>
            <button onClick={downloadSvg} className="btn-ghost" data-testid="qr-download-svg"><Download className="w-4 h-4" /> Download SVG</button>
          </div>
        </div>

        <div className="card-shell p-6 flex items-center justify-center" style={{ background: '#070709' }}>
          <div className="p-6 rounded-xl" style={{ background: bg }} data-testid="qr-preview">
            <QRCodeSVG id="qr-svg" value={value || ' '} size={size} bgColor={bg} fgColor={fg} level={level} />
            <div ref={canvasRef} className="hidden">
              <QRCodeCanvas value={value || ' '} size={size} bgColor={bg} fgColor={fg} level={level} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
